// ─────────────────────────────────────────────────────────────────────────────
// THE BROWSER'S OWN BOTTOM BAR — a SECOND inset, and not the safe area.
//
// The 2026-08-28 safe-area pass fixed the HOME INDICATOR: env(safe-area-inset-
// bottom) plus viewport-fit=cover, which is the right and complete answer for a
// home-screen (standalone) install. It does nothing for the case reported off a
// phone on 2026-08-30 — BoneFlow open as an ORDINARY SAFARI TAB, where Safari's
// own toolbar sits along the bottom of the screen and cuts the bug-report
// launcher and the profile panel's Log Out in half.
//
// Those are different problems with different geometry, and the safe area
// genuinely cannot cover this one: in a Safari tab in portrait, Safari's own
// chrome already occupies the home-indicator strip, so it reports
// safe-area-inset-bottom as 0px. Correctly — there IS no unsafe display area
// left for the page to avoid. The page is simply shorter than the page thinks.
//
// ── THE TWO HALVES, AND WHY BOTH ────────────────────────────────────────────
//
// (1) HEIGHTS ARE dvh, NOT vh. `100vh` is the LARGE viewport — the height the
//     page would have if every dynamic toolbar were retracted — so a fixed,
//     top-anchored `height: 100vh` overlay in a Safari tab is exactly one
//     toolbar TALLER than the screen, and whatever it pins to its own bottom
//     edge (the profile panel pins Log Out there) falls off. `100dvh` is the
//     CURRENT height, which is what "fill the screen" was always supposed to
//     mean. That is a one-unit change and it needs nothing from this file.
//
// (2) THIS FILE, for things anchored to `bottom:` — the bug launcher, the new
//     "+" launcher, a bottom sheet's last row. Those resolve against the
//     initial containing block, and no CSS unit lets an element ask "how much
//     of the ICB is currently underneath browser furniture". So it is
//     MEASURED, and published as a custom property any inline style can read:
//
//         bottom: calc(14px + env(safe-area-inset-bottom, 0px)
//                           + var(--bf-bottom-chrome, 0px))
//
// ── WHY MEASURED AND NOT calc(100lvh - 100dvh) ──────────────────────────────
//
// That expression is the tempting CSS-only version of the same idea, and it is
// only correct if the ICB is sized to the LARGE viewport. If a browser sizes it
// to the small or the current one instead, the expression pushes controls up by
// a toolbar height that is not there — a regression invented out of an
// assumption, on a platform this repo cannot drive. Measuring cannot do that:
// the reading is a DIFFERENCE between two things the browser reports about
// itself, so where there is no overlapping chrome it is 0 and nothing moves.
// Every desktop browser, every standalone install, and Android's top-anchored
// URL bar all read 0 and are untouched by construction, not by a gate.
//
// This is the same "measure it, don't guess at one handset" rule the safe-area
// pass and PullToRefresh's measureTopInset() already follow — a throwaway
// element is the read, because env()/percentages only resolve against a
// laid-out box.
// ─────────────────────────────────────────────────────────────────────────────

export const BOTTOM_CHROME_VAR = '--bf-bottom-chrome';

// One percent of the VISIBLE viewport — dvh where the browser has it, the vh it
// always had where it does not. See the --bf-vh block in src/index.css for why
// the fallback lives in an @supports rule rather than a duplicate declaration:
// an inline style object cannot hold the same key twice, and every consumer of
// this is a per-call-site number arriving as an inline style.
export const VIEWPORT_UNIT_VAR = '--bf-vh';

/**
 * A percentage of the visible viewport height, as a CSS length.
 *
 *   vh(92)  ->  calc(92 * var(--bf-vh))
 *
 * Use it for a FIXED, SCROLL-LOCKED surface — a sheet, a modal, a bottom-
 * anchored prompt stack. Do NOT use it for page flow: dvh re-resolves while
 * Safari's toolbar collapses mid-scroll, so a scrolling page sized in it would
 * reflow under the reader. The app shell keeps `minHeight: 100vh` on purpose.
 */
export function vh(percent) {
  return `calc(${percent} * var(${VIEWPORT_UNIT_VAR}))`;
}

// A real bottom toolbar is ~32px (landscape) to ~52px (portrait). The clamp is
// not padding: on Android a soft keyboard shrinks window.innerHeight while the
// ICB stays put, which would otherwise read as several hundred pixels of
// "chrome" and throw a launcher into the middle of the screen. The focus guard
// in the watcher below is the first line of defence; this is the second.
const MAX_CHROME = 120;

/**
 * The height of the initial containing block — the box a fixed element's
 * `bottom:` is measured from. height:100% on a position:fixed element resolves
 * against exactly that, so a throwaway element is the read (the same technique
 * PullToRefresh's measureTopInset uses, and for the same reason: this cannot be
 * asked for in the abstract, only of a laid-out box).
 */
function measureICB() {
  if (typeof window === 'undefined' || !document.body) return 0;
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;top:0;left:0;width:0;height:100%;visibility:hidden;pointer-events:none';
  document.body.appendChild(probe);
  const h = probe.getBoundingClientRect().height;
  document.body.removeChild(probe);
  return Number.isFinite(h) ? h : 0;
}

/**
 * Pixels of browser furniture currently overlapping the BOTTOM of the initial
 * containing block — i.e. how far `bottom: 0` now sits below what the user can
 * actually see. 0 wherever there is none.
 */
export function measureBottomChrome(icbHeight) {
  if (typeof window === 'undefined') return 0;
  const icb = Number.isFinite(icbHeight) ? icbHeight : measureICB();
  const visible = window.innerHeight;
  if (!icb || !Number.isFinite(visible)) return 0;
  return Math.max(0, Math.min(MAX_CHROME, Math.round(icb - visible)));
}

// A focused field means a soft keyboard may be up, and on some platforms that
// moves the very numbers this reads. The last good value is kept until it is
// dismissed — browser chrome does not change while you are typing anyway.
function keyboardMayBeUp() {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

/**
 * Keep BOTTOM_CHROME_VAR on <html> in step with the browser's bottom bar.
 * Returns a stop function. Safe to call in any environment — a server render
 * or a browser without visualViewport simply gets a no-op teardown.
 */
export function startBottomChromeWatch() {
  if (typeof window === 'undefined' || !document.documentElement) return () => {};
  const root = document.documentElement;
  let last = null;
  let icb = measureICB();
  let raf = null;

  const write = () => {
    raf = null;
    if (keyboardMayBeUp()) return;
    const px = measureBottomChrome(icb);
    if (px === last) return;
    last = px;
    root.style.setProperty(BOTTOM_CHROME_VAR, `${px}px`);
  };
  // Coalesced to one write per frame: Safari fires a burst of these while its
  // toolbar animates.
  const schedule = () => { if (raf == null) raf = requestAnimationFrame(write); };

  // THE ICB IS ONLY RE-MEASURED ON A GEOMETRY CHANGE, NOT ON SCROLL, and that
  // split is the whole reason this is cheap. The probe is a DOM insertion plus
  // a forced layout; running it per scroll event would put one of those in
  // front of every frame of every list on the app's most performance-sensitive
  // surface. The ICB does not change while scrolling — only window.innerHeight
  // does, as the toolbar collapses — so the scroll path reads that alone.
  const onGeometry = () => { icb = measureICB(); schedule(); };

  write();
  window.addEventListener('resize', onGeometry);
  window.addEventListener('orientationchange', onGeometry);
  // Safari collapses and re-expands its toolbar DURING a scroll without always
  // firing `resize` for it. Passive, coalesced, and a no-op unless the number
  // actually moved.
  window.addEventListener('scroll', schedule, { passive: true });
  const vv = window.visualViewport;
  if (vv) {
    vv.addEventListener('resize', schedule);
    vv.addEventListener('scroll', schedule);
  }
  // The keyboard guard above HOLDS the last value while a field is focused;
  // this is what picks the real one back up once the keyboard goes away.
  document.addEventListener('focusout', schedule);

  return () => {
    if (raf != null) cancelAnimationFrame(raf);
    window.removeEventListener('resize', onGeometry);
    window.removeEventListener('orientationchange', onGeometry);
    window.removeEventListener('scroll', schedule);
    if (vv) {
      vv.removeEventListener('resize', schedule);
      vv.removeEventListener('scroll', schedule);
    }
    document.removeEventListener('focusout', schedule);
    root.style.removeProperty(BOTTOM_CHROME_VAR);
  };
}

/**
 * The CSS a fixed, bottom-anchored control should use for its `bottom`:
 * its own clearance, plus the device's home indicator, plus the browser's bar.
 * All three collapse to just `base` where the last two are 0.
 */
export function bottomClear(base = '0px') {
  return `calc(${base} + env(safe-area-inset-bottom, 0px) + var(${BOTTOM_CHROME_VAR}, 0px))`;
}
